import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import ModalContext from 'contexts/ModalContext'
import {
  getCustomerLanguagePreferenceFromAuth,
  updateCustomerLanguagePreferenceFromAuth,
} from 'api/auth'
import { syncUserDetails } from 'actions/AuthActions'
import { SET_REQUEST_LOCALE } from 'actions/actionConstants'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { getLocaleFromPathname, replaceLocaleInUrl } from 'i18n/util/localeUrl'
import { isEnabledLocale } from 'i18n/util/localeMatcher'
import { getEnabledLanguageLocales } from 'i18n/supportedLanguageOptions'
import { DEFAULT_LOCALE } from 'i18n/util/locale'
import { useLabRemoteConfig } from 'hooks/FeatureFlag/useLabRemoteConfig'
import { LabRemoteConfigKeys } from 'constants/lab'
import setLanguageCookie from 'cookies/setLanguageCookie'
import * as isomorphicSessionStorage from 'lib/storage/isomorphicSessionStorage'
import {
  reportClientError,
  reportClientMessage,
} from 'services/errorReportingService'
import LocaleRedirectModal from 'components/Common/Modals/LocaleRedirectModal/LocaleRedirectModal'

type ConflictChoice = 'url' | 'auth'

function getConflictSessionKey(
  memberId: string,
  urlLocale: string,
  authLocale: string,
): string {
  return `auth-backed-locale-conflict:${memberId}:${urlLocale}:${authLocale}`
}

function reportUnsupportedAuthPreference(memberId: string, language: string) {
  reportClientMessage(
    'auth_language_preference_unsupported',
    { memberId, language },
    'warning',
    { domain: 'i18n', surface: 'auth_language_preference' },
  )
}

/**
 * Reconciles the authenticated customer language preference with the URL locale
 * after hydration. Clean URLs stay clean for auth-only preferences, explicit URL
 * locales remain the active page-language signal, URL-only preferences are
 * written back to auth, and conflicts are resolved through a one-per-session
 * confirmation modal.
 */
export default function AuthBackedLocalePreferenceSync() {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const memberId = useAppSelector((state) => state.auth.account.memberId)
  const accountLanguage = useAppSelector(
    (state) => state.auth.account.language,
  )
  const requestLocale = useAppSelector((state) => state.geo.locale)
  const languagesRemoteConfig = useLabRemoteConfig(
    LabRemoteConfigKeys.languages,
  )
  const enabledLanguageLocales = useMemo(
    () => getEnabledLanguageLocales(languagesRemoteConfig),
    [languagesRemoteConfig],
  )

  const navigateToLocale = useCallback(
    (locale: string) => {
      dispatch({ type: SET_REQUEST_LOCALE, locale })

      const currentLocationUrl = `${location.pathname}${location.search}${location.hash}`
      const targetUrl = replaceLocaleInUrl(currentLocationUrl, locale)
      setLanguageCookie(locale)
      if (targetUrl !== currentLocationUrl) {
        // Force a full navigation so app bootstrap, SSR data, and locale-aware routing re-run for the URL locale.
        window.location.assign(targetUrl)
      }
    },
    [dispatch, location],
  )

  const writeAccountLocale = useCallback(
    async(locale: string, signal?: AbortSignal) => {
      try {
        await updateCustomerLanguagePreferenceFromAuth(locale, signal)
        if (signal?.aborted) return

        dispatch(syncUserDetails({ language: locale }))
      } catch(error) {
        reportClientError(error)
      }
    },
    [dispatch],
  )

  useEffect(() => {
    const urlLocale = getLocaleFromPathname(location.pathname)
    if (
      (!isLoggedIn || !memberId) &&
      !urlLocale &&
      requestLocale &&
      requestLocale !== DEFAULT_LOCALE &&
      isEnabledLocale(requestLocale, enabledLanguageLocales)
    ) {
      navigateToLocale(requestLocale)
      return
    }

    if (!isLoggedIn || !memberId) return

    const authenticatedMemberId = memberId
    const abortController = new AbortController()
    const { signal } = abortController

    // Redux auth state can be hydrated from stale SSR/session data, so conflict
    // checks need a fresh preference read before prompting or writing back.
    async function getSupportedAuthLocalePreference(): Promise<
      string | null | undefined
    > {
      let authPreference: string | null
      try {
        authPreference = await getCustomerLanguagePreferenceFromAuth(signal)
      } catch(error) {
        reportClientError(error)
        return undefined
      }

      if (
        authPreference &&
        !isEnabledLocale(authPreference, enabledLanguageLocales)
      ) {
        reportUnsupportedAuthPreference(authenticatedMemberId, authPreference)
        return undefined
      }

      return authPreference
    }

    const shouldFetchAuthPreference = accountLanguage === undefined
    const authPreferencePromise = shouldFetchAuthPreference
      ? getSupportedAuthLocalePreference()
      : Promise.resolve(accountLanguage)

    authPreferencePromise.then(async(authPreference) => {
      if (signal.aborted) return

      if (authPreference === undefined) {
        return
      }

      if (
        !shouldFetchAuthPreference &&
        authPreference &&
        !isEnabledLocale(authPreference, enabledLanguageLocales)
      ) {
        reportUnsupportedAuthPreference(authenticatedMemberId, authPreference)
        return
      }

      let authLocale = authPreference
      if (!urlLocale) {
        if (authLocale) {
          if (requestLocale && requestLocale !== authLocale) {
            navigateToLocale(authLocale)
          } else {
            setLanguageCookie(authLocale)
            dispatch({ type: SET_REQUEST_LOCALE, locale: authLocale })
          }
        } else if (
          requestLocale &&
          requestLocale !== DEFAULT_LOCALE &&
          isEnabledLocale(requestLocale, enabledLanguageLocales)
        ) {
          navigateToLocale(requestLocale)
        }
        return
      }

      if (!isEnabledLocale(urlLocale, enabledLanguageLocales)) return

      if (!authLocale) {
        setLanguageCookie(urlLocale)
        await writeAccountLocale(urlLocale, signal)
        if (signal.aborted) return

        dispatch({ type: SET_REQUEST_LOCALE, locale: urlLocale })
        return
      }

      if (urlLocale === authLocale) {
        setLanguageCookie(urlLocale)
        dispatch({ type: SET_REQUEST_LOCALE, locale: urlLocale })
        return
      }

      if (!shouldFetchAuthPreference) {
        const freshAuthLocale = await getSupportedAuthLocalePreference()
        if (signal.aborted || freshAuthLocale === undefined) return

        if (!freshAuthLocale) {
          setLanguageCookie(urlLocale)
          await writeAccountLocale(urlLocale, signal)
          if (signal.aborted) return
          dispatch({ type: SET_REQUEST_LOCALE, locale: urlLocale })
          return
        }

        // prevent stale Redux auth store from triggering conflict modal
        // when user just changed language through navbar
        if (urlLocale === freshAuthLocale) {
          dispatch(syncUserDetails({ language: freshAuthLocale }))
          dispatch({ type: SET_REQUEST_LOCALE, locale: urlLocale })
          return
        }

        authLocale = freshAuthLocale
      }

      const conflictSessionKey = getConflictSessionKey(
        authenticatedMemberId,
        urlLocale,
        authLocale,
      )
      if (isomorphicSessionStorage.get(conflictSessionKey)) return
      isomorphicSessionStorage.set(conflictSessionKey, true)

      let choice: ConflictChoice | undefined
      try {
        choice = await showModal<ConflictChoice>(
          <LocaleRedirectModal urlLocale={urlLocale} authLocale={authLocale} />,
        )
      } catch(error) {
        reportClientError(error)
        return
      }

      if (signal.aborted) return

      if (choice === 'url') {
        setLanguageCookie(urlLocale)
        await writeAccountLocale(urlLocale, signal)
        if (signal.aborted) return
        dispatch({ type: SET_REQUEST_LOCALE, locale: urlLocale })
      } else if (choice === 'auth') {
        navigateToLocale(authLocale)
      }
    })

    return () => {
      abortController.abort()
    }
  }, [
    accountLanguage,
    dispatch,
    enabledLanguageLocales,
    isLoggedIn,
    location.pathname,
    memberId,
    navigateToLocale,
    requestLocale,
    showModal,
    writeAccountLocale,
  ])

  return null
}
